"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Briefcase, TrendingUp, Award, Sparkles } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchAboutContacts,
  fetchClientStats,
} from "@/store/slices/landingSlice";

// ─── Animated Counter ─────────────────────────────────────────────────────────
const Counter = ({
  value,
  duration = 1.8,
}: {
  value: string;
  duration?: number;
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const numeric = parseFloat(value.replace(/[^0-9.]/g, ""));
    const suffix = value.replace(/[0-9.]/g, "");
    if (isNaN(numeric)) {
      setDisplay(value);
      return;
    }

    let start = 0;
    const steps = 60;
    const increment = numeric / steps;
    const interval = (duration * 1000) / steps;
    const timer = setInterval(() => {
      start += increment;
      if (start >= numeric) {
        setDisplay(`${numeric}${suffix}`);
        clearInterval(timer);
      } else setDisplay(`${Math.floor(start)}${suffix}`);
    }, interval);
    return () => clearInterval(timer);
  }, [inView, value, duration]);

  return <span ref={ref}>{display}</span>;
};

// ─── Word-split animation ─────────────────────────────────────────────────────
const AnimatedWords = ({
  text,
  className,
  delay = 0,
}: {
  text: string;
  className?: string;
  delay?: number;
}) => (
  <span className={className}>
    {text.split(" ").map((word, i) => (
      <motion.span
        key={i}
        className="inline-block mr-[0.25em]"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.55,
          ease: [0.22, 1, 0.36, 1],
          delay: delay + i * 0.08,
        }}
      >
        {word}
      </motion.span>
    ))}
  </span>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const PortfolioHeroSection = () => {
  const dispatch = useAppDispatch();
  const { data: aboutData } = useAppSelector((s) => s.landing.aboutContact);
  const { data: statsData } = useAppSelector((s) => s.landing.clientStats);
  const { mode } = useAppSelector((s) => s.theme);
  const isDark = mode === "dark";
  const firstAbout = aboutData[0];

  useEffect(() => {
    dispatch(fetchAboutContacts());
    dispatch(fetchClientStats());
  }, [dispatch]);

  const purpleGlow = isDark
    ? "shadow-[0_0_24px_rgba(124,58,237,0.3)]"
    : "shadow-[0_4px_20px_rgba(79,17,194,0.15)]";

  const stats = [
    {
      icon: Briefcase,
      number: statsData[1]?.value || "100+",
      label: statsData[1]?.label || "Projects Completed",
      gradient: "from-[#8B31FF] to-[#8B31FF]",
      glow: purpleGlow,
    },
    {
      icon: TrendingUp,
      number: "98%",
      label: "Client Satisfaction",
      gradient: "from-[#8B31FF] to-[#a855f7]",
      glow: purpleGlow,
    },
    {
      icon: Award,
      number: "25+",
      label: "Industry Awards",
      gradient: "from-[#8B31FF] to-[#8B31FF]",
      glow: purpleGlow,
    },
  ];

  const titleText = firstAbout?.mainTitle || "Our Work Speaks Volumes";
  const subtitleText =
    firstAbout?.subtitle ||
    "Explore our collection of successful projects across diverse industries. Each project represents our commitment to excellence and innovation.";

  return (
    <section
      className={`relative flex flex-col items-center justify-center overflow-hidden
        transition-colors duration-700 pt-32 sm:pt-36 md:pt-40 pb-12 sm:pb-16
        ${isDark ? "bg-[#0B0F19]" : "bg-slate-50"}`}
    >
      {/* Top accent line */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-violet-500 to-transparent" />

      {/* ── Background ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute top-0 -left-64 w-[500px] h-[500px] rounded-full blur-[100px] transition-all duration-700 ${isDark ? "bg-[#8B31FF]/20 opacity-100" : "bg-[#8B31FF]/10 opacity-50"}`}
        />
        <div
          className={`absolute bottom-0 -right-64 w-[500px] h-[500px] rounded-full blur-[100px] transition-all duration-700 ${isDark ? "bg-[#2B00A4]/20 opacity-100" : "bg-[#2B00A4]/10 opacity-50"}`}
        />
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${isDark ? "opacity-[0.03]" : "opacity-[0.05]"}`}
          style={{
            backgroundImage: isDark
              ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
              : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      {/* ── Content ── */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className={`inline-flex items-center gap-2 mb-7 px-5 py-2 rounded-full border backdrop-blur-sm
            ${
              isDark
                ? "bg-violet-500/10 border-violet-500/25 text-violet-400"
                : "bg-violet-50 border-violet-200 text-violet-600"
            }`}
        >
          <Sparkles className="w-3.5 h-3.5" />
          <span className="text-xs font-bold uppercase tracking-widest">
            Our Portfolio
          </span>
        </motion.div>

        {/* Title */}
        <h1
          className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-[1.12]
            ${isDark ? "text-white" : "text-gray-900"}`}
        >
          <AnimatedWords
            text={titleText.split(" ").slice(0, -1).join(" ")}
            delay={0.1}
          />{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] via-[#a855f7] to-[#8B31FF] whitespace-nowrap">
            <AnimatedWords
              text={titleText.split(" ").slice(-1)[0]}
              delay={0.1 + (titleText.split(" ").length - 1) * 0.08}
            />
          </span>
        </h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className={`text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-14
            ${isDark ? "text-gray-400" : "text-gray-500"}`}
        >
          {subtitleText}
        </motion.p>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-5"
        >
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -5, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 350, damping: 28 }}
              className={`relative group rounded-lg p-7 border overflow-hidden
                transition-all duration-300 ${stat.glow}
                ${
                  isDark
                    ? "bg-white/[0.04] border-white/[0.08] hover:border-white/[0.16]"
                    : "bg-white border-gray-200/80 hover:border-violet-200"
                }`}
            >
              {/* Background gradient accent */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0
                  group-hover:opacity-[0.06] transition-opacity duration-300`}
              />

              {/* Icon circle */}
              <div
                className={`inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4
                bg-gradient-to-br ${stat.gradient}`}
              >
                <stat.icon className="w-5 h-5 text-white" />
              </div>

              {/* Number */}
              <div
                className={`text-4xl md:text-5xl font-extrabold mb-1.5 leading-none
                ${isDark ? "text-white" : "text-gray-900"}`}
              >
                <Counter value={stat.number} />
              </div>

              {/* Label */}
              <p
                className={`text-sm font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                {stat.label}
              </p>

              {/* Bottom gradient line */}
              <div
                className={`absolute bottom-0 inset-x-0 h-[2px] bg-gradient-to-r ${stat.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default PortfolioHeroSection;
